import { useTranslation } from 'react-i18next';

import { Box, Button, IconWarning } from '@myphx-engineering/phx-design-system';

import { type PriorAuthorizationInfoFragment } from '../../../graphql/generated/graphql';
import { usePriorAuthorizationDetails } from '../../../hooks/use-prior-authorization-details';
import type { BasicPrescriberOrderFragment } from '../../../util/cabinet/prescriber-orders';

type MedicationOrderStateDisplayProps = {
    isUnclaimed: boolean;
    verifyOrder: () => void;
    order: BasicPrescriberOrderFragment | PriorAuthorizationInfoFragment;
    fillOrder: () => void;
};

export const MedicationOrderStateDisplay = ({
    isUnclaimed,
    verifyOrder,
    order,
    fillOrder,
}: MedicationOrderStateDisplayProps) => {
    const { t } = useTranslation();
    const {
        isNonDrxPriorAuthorization,
        priorAuthorizationDetails,
        isApprovedPriorAuthorization,
    } = usePriorAuthorizationDetails(order);

    if (isNonDrxPriorAuthorization) {
        return (
            <Box>
                <IconWarning color="warning" />
            </Box>
        );
    }

    if (isUnclaimed) {
        return (
            <Box>
                <Button
                    variant="outline"
                    onClick={verifyOrder}
                    size="sm"
                    data-testid="medication-entry-verify-button"
                >
                    {t('cabinet.orders.verify')}
                </Button>
            </Box>
        );
    }

    // Note: Allow Fill when it's an approved non-specialty dRx PA or just new dRx
    const showFillButton =
        !priorAuthorizationDetails ||
        (isApprovedPriorAuthorization &&
            !priorAuthorizationDetails.isSpecialty);

    if (showFillButton) {
        return (
            <Box>
                <Button
                    variant="contained"
                    onClick={fillOrder}
                    size="sm"
                    data-testid="medication-entry-fill-button"
                >
                    {t('cabinet.orders.fill')}
                </Button>
            </Box>
        );
    }

    return null;
};
